'use client'

import { Box, DesignSystemProvider, theme } from '@adamdotai/design-system'
import { useParams } from 'next/navigation'
import { SessionProvider } from 'next-auth/react'
import type { Locale } from 'next-intl'
import { Suspense } from 'react'
import { PageErrorBoundaryFallback } from '@/components/ErrorBoundary/PageErrorBoundaryFallback'
import { lato, notoKufiArabic, outfit, vazirmatn } from '@/utils/fonts'
import AnalyticsImpl from './providers/analytics/analytics-impl'
import { CustomNextIntlClientProvider } from './providers/custom-i18n'
import { UrqlClientProvider } from './providers/urql-client'

function ErrorDesignSystemProvider({
    children,
    locale,
}: {
    children: React.ReactNode
    locale: Locale
}) {
    const isRTL = locale === 'ar'

    return (
        <DesignSystemProvider isRTL={isRTL} locale={locale}>
            {children}
        </DesignSystemProvider>
    )
}

type Props = {
    error: Error & { digest?: string }
    reset: () => void
}

export default function Error({ error, reset }: Props) {
    const { locale } = useParams<{ locale: Locale }>()
    const dir = locale === 'ar' ? 'rtl' : 'ltr'

    return (
        <html
            suppressHydrationWarning
            lang={locale}
            dir={dir}
            className={`${lato.variable} ${notoKufiArabic.variable} ${outfit.variable} ${vazirmatn.variable}`}
        >
            <body>
                <SessionProvider>
                    <UrqlClientProvider>
                        <CustomNextIntlClientProvider>
                            <ErrorDesignSystemProvider locale={locale}>
                                <Box
                                    sx={{
                                        minW: theme.sizes[320],
                                        height: '100vh',

                                        [`@media only screen and (max-width: ${theme.sizes[320]})`]:
                                            {
                                                height: '99vh',
                                            },
                                    }}
                                >
                                    <PageErrorBoundaryFallback
                                        error={error}
                                        reset={reset}
                                    />
                                </Box>
                            </ErrorDesignSystemProvider>
                            <Suspense fallback={null}>
                                <AnalyticsImpl />
                            </Suspense>
                        </CustomNextIntlClientProvider>
                    </UrqlClientProvider>
                </SessionProvider>
            </body>
        </html>
    )
}
